/**
 * Star Toner — Language Switcher Pro (Phase 6 EU launch)
 *
 * Handles open/close, keyboard nav, focus trap, click-outside,
 * mobile bottom-sheet with backdrop, and form submission.
 *
 * Initialised on DOMContentLoaded. Idempotent — safe if loaded twice.
 */

(function () {
  'use strict';

  if (window.__stlsInitialized__) return;
  window.__stlsInitialized__ = true;

  const STORAGE_KEY = 'startoner_preferred_locale';
  const DEFAULT_LOCALE = 'es';
  const LOCALE_PREFIXES = ['en', 'fr', 'de', 'it', 'nl', 'pt', 'sv', 'fi', 'pl'];
  const DISTRIBUTOR_PATH_RE = /^\/(en|fr|de|it|nl|pt)\/pages\/conviertete-en-distribuidores\/?$/;
  const STALE_DISTRIBUTOR_RE = /Crece con|Formulario distribuidor|material de oficina|Más de 2\.000/;

  function refreshStaleDistributorPage() {
    if (!DISTRIBUTOR_PATH_RE.test(window.location.pathname)) return;
    if (/[?&]stloc=1&v=20260510(?:&|$)/.test(window.location.search)) return;

    const text = document.body ? document.body.textContent || '' : '';
    if (!STALE_DISTRIBUTOR_RE.test(text)) return;

    const joiner = window.location.search ? '&' : '?';
    window.location.replace(window.location.pathname + window.location.search + joiner + 'stloc=1&v=20260510' + window.location.hash);
  }

  function normalizeLocale(value) {
    if (!value) return '';
    return String(value).toLowerCase().split('-')[0];
  }

  function currentLocaleFromPath() {
    const first = window.location.pathname.split('/').filter(Boolean)[0];
    return LOCALE_PREFIXES.includes(first) ? first : DEFAULT_LOCALE;
  }

  function preferredLocale() {
    const current = currentLocaleFromPath();
    if (current !== DEFAULT_LOCALE) return current;

    try {
      const saved = normalizeLocale(window.localStorage.getItem(STORAGE_KEY));
      if (LOCALE_PREFIXES.includes(saved)) return saved;
    } catch (e) {}

    return current;
  }

  function pathWithLocale(path, locale) {
    if (!path || path[0] !== '/') return path;
    if (path.startsWith('//')) return path;

    const parts = path.split('/');
    const first = parts[1];

    if (LOCALE_PREFIXES.includes(first)) {
      parts.splice(1, 1);
      path = parts.join('/') || '/';
    }

    if (!locale || locale === DEFAULT_LOCALE) return path;
    return '/' + locale + (path === '/' ? '' : path);
  }

  function preserveLocaleOnInternalLinks() {
    const locale = preferredLocale();
    if (!locale || locale === DEFAULT_LOCALE) return;

    document.documentElement.setAttribute('data-st-preferred-locale', locale);

    document.querySelectorAll('a[href]').forEach((link) => {
      let raw = link.getAttribute('href');
      if (!raw || raw[0] !== '/' || raw.startsWith('//')) return;
      if (raw.startsWith('/cdn/') || raw.startsWith('/admin') || raw.startsWith('/apps/')) return;
      if (raw.indexOf('#shopify') === 0) return;
      if (/^\/(en|fr|de|it|nl|pt)\/pages\/conviertete-en-distribuidores\/?$/.test(raw)) {
        raw = raw + '?stloc=1&v=20260510';
      }

      const next = pathWithLocale(raw, locale);
      if (next !== raw) link.setAttribute('href', next);
    });
  }

  function init() {
    const disclosures = document.querySelectorAll('[data-stls-disclosure]');
    preserveLocaleOnInternalLinks();
    if (!disclosures.length) return;

    disclosures.forEach((disclosure) => {
      const trigger = disclosure.querySelector('[data-stls-trigger]');
      const panel = disclosure.querySelector('[data-stls-panel]');
      if (!trigger || !panel) return;

      const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

      let backdrop = null;
      function ensureBackdrop() {
        if (backdrop) return backdrop;
        backdrop = document.createElement('div');
        backdrop.className = 'stls__backdrop';
        backdrop.setAttribute('data-stls-backdrop', '');
        document.body.appendChild(backdrop);
        backdrop.addEventListener('click', () => close());
        return backdrop;
      }

      function open() {
        panel.removeAttribute('hidden');
        // Force reflow so the transform/opacity transition fires
        // eslint-disable-next-line no-unused-expressions
        panel.offsetHeight;
        trigger.setAttribute('aria-expanded', 'true');
        disclosure.setAttribute('data-open', 'true');

        if (isMobile()) {
          ensureBackdrop().setAttribute('data-visible', 'true');
          document.body.classList.add('stls-locked');
        }

        // Move focus to current item or first item
        const current = panel.querySelector('.stls__option.is-current');
        const target = current || panel.querySelector('.stls__option');
        if (target) {
          // Slight delay so the animation kicks in before focus shift
          setTimeout(() => target.focus(), 60);
        }
      }

      function close() {
        panel.setAttribute('hidden', '');
        trigger.setAttribute('aria-expanded', 'false');
        disclosure.removeAttribute('data-open');

        if (backdrop) {
          backdrop.removeAttribute('data-visible');
        }
        document.body.classList.remove('stls-locked');
      }

      function toggle(e) {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (isOpen) close();
        else open();
      }

      trigger.addEventListener('click', toggle);

      // Click outside to close
      document.addEventListener('click', (e) => {
        if (!disclosure.contains(e.target) && !(backdrop && backdrop.contains(e.target))) {
          close();
        }
      });

      // ESC closes + restores focus to trigger
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') {
          close();
          trigger.focus();
        }
      });

      // Arrow keys + Home/End within list (focus trap)
      panel.addEventListener('keydown', (e) => {
        const options = Array.from(panel.querySelectorAll('.stls__option'));
        if (!options.length) return;
        const idx = options.indexOf(document.activeElement);

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          options[(idx + 1) % options.length].focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          options[(idx - 1 + options.length) % options.length].focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          options[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          options[options.length - 1].focus();
        } else if (e.key === 'Tab') {
          // Trap focus inside panel when on mobile bottom-sheet
          if (isMobile()) {
            if (e.shiftKey && idx === 0) {
              e.preventDefault();
              options[options.length - 1].focus();
            } else if (!e.shiftKey && idx === options.length - 1) {
              e.preventDefault();
              options[0].focus();
            }
          }
        }
      });

      disclosure.addEventListener('submit', (e) => {
        const submitter = e.submitter;
        const locale = normalizeLocale(submitter && submitter.value);
        try {
          if (locale && locale !== DEFAULT_LOCALE) window.localStorage.setItem(STORAGE_KEY, locale);
          if (locale === DEFAULT_LOCALE) window.localStorage.removeItem(STORAGE_KEY);
        } catch (err) {}
      });

      // On orientation/viewport change while open, recalc backdrop
      window.addEventListener('resize', () => {
        if (trigger.getAttribute('aria-expanded') === 'true') {
          if (!isMobile() && backdrop) {
            backdrop.removeAttribute('data-visible');
            document.body.classList.remove('stls-locked');
          }
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      refreshStaleDistributorPage();
      init();
    });
  } else {
    refreshStaleDistributorPage();
    init();
  }
})();

(function () {
  function normalizeHref(href) {
    if (!href) return href;
    var next = href;

    next = next.replace(/^https:\/\/star-toner\.com/i, 'https://www.star-toner.com');
    next = next.replace(/\/policies\/cookie-policy\b/i, '/pages/cookie-policy');
    next = next.replace(/\/pages\/Kontakt\b/g, '/pages/contacto-startoner');
    next = next.replace(/\/pages\/contatto\b/g, '/pages/contacto-startoner');
    next = next.replace(/\/blogs\/news\/cartucho-de-tinta-o-toner-cual-elegir-segun-tu-necesidad\b/g, '/blogs/news/cartucho-de-tinta-o-toner');
    next = next.replace(/\/blogs\/news\/impresora-barata-para-casa-la-guia-que-los-fabricantes-no-quieren-que-leas\b/g, '/blogs/news/impresora-barata-para-casa');

    if (next.indexOf('{{ routes.root_url }}') !== -1) {
      next = next
        .replace(/\/products\/\{\{\s*routes\.root_url\s*\}\}\//g, '/')
        .replace(/\{\{\s*routes\.root_url\s*\}\}/g, '');
    }

    next = next
      .replace(/\/products\/%7B%7B%20routes\.root_url%20%7D%7D\/?/ig, '/')
      .replace(/\/products\/%7B%7Broutes\.root_url%7D%7D\/?/ig, '/');

    return next;
  }

  function normalizeLinks(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('a[href]'), function (a) {
      var href = a.getAttribute('href');
      var next = normalizeHref(href);
      if (next && next !== href) a.setAttribute('href', next);
    });
  }

  function run() {
    normalizeLinks(document);
    if (!('MutationObserver' in window)) return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (!node || node.nodeType !== 1) return;
          if (node.matches && node.matches('a[href]')) {
            var href = node.getAttribute('href');
            var next = normalizeHref(href);
            if (next && next !== href) node.setAttribute('href', next);
          }
          if (node.querySelectorAll) normalizeLinks(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();

(function () {
  var css = '.hero-pro .hero-pro-text-stack{display:contents!important}' +
    '.hero-pro .hero-pro-eyebrow{display:none!important}' +
    '.hero-pro .hp-proof-row{order:1!important;margin:0 0 24px!important;justify-content:center!important}' +
    '.hero-pro .hero-pro-h1{order:2!important}' +
    '.hero-pro .hero-pro-sub{order:3!important}' +
    '.hero-pro .hero-pro-btns{order:4!important}' +
    '.hero-pro .hp-trust-cluster{order:5!important}' +
    '@media(min-width:901px){.hero-pro .hp-proof-row{justify-content:flex-start!important}}';
  var style = document.createElement('style');
  style.setAttribute('data-st-hero-proof-position', 'true');
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
})();

(function () {
  var byLocale = {
    en: {
      'Quiénes Somos': 'About us',
      'Conozca nuestro equipo': 'Meet our team',
      'Nuestro Blog': 'Our Blog',
      'Noticias y Novedades': 'News and updates',
      'Contact Rápido': 'Quick Contact',
      'Contacto Rápido': 'Quick Contact',
      'Línea Directa de Support': 'Direct support line',
      'Línea Directa de Soporte': 'Direct support line',
      'Servicio Técnico': 'Technical Service',
      'Ofertas Activas': 'Active Deals',
      'Contactar Support': 'Contact Support',
      'Contactar Soporte': 'Contact Support',
      'Ayuda': 'Help',
      'Preguntas Frecuentes': 'Frequently Asked Questions',
      'Política de Devoluciones': 'Returns Policy',
      'Envíos y Entregas': 'Shipping & Delivery',
      'Reembolsos': 'Refunds',
      'Buscar referencias': 'Find references',
      'Guías y enlaces clave': 'Guides and key links',
      'Cartuchos HP 364, 903 y 953XL': 'HP Cartridges 364, 903 and 953XL',
      'Cartucho o tóner: cuál elegir': 'Ink or toner: which to choose',
      'Cómo saber qué tóner necesita tu impresora': 'How to know which toner your printer needs',
      'Cómo alargar la vida de tus cartuchos': 'How to extend cartridge life',
      'Firmware HP y cartuchos compatibles': 'HP firmware and compatible cartridges',
      'EcoTank vs MegaTank: comparativa': 'EcoTank vs MegaTank: comparison',
      'Impresora térmica para negocios': 'Thermal printer for business',
      'Por qué el papel importa al imprimir': 'Why paper matters when printing',
      'Privacidad': 'Privacy',
      'Términos': 'Terms',
      'Aviso legal': 'Legal notice',
      'Información legal': 'Legal information',
      'Resolución de litigios UE': 'EU dispute resolution',
      'Inscrita en el Registro Mercantil de Cádiz, Tomo 2465, Folio 113, Sección 8, Hoja CA-61491': 'Registered in the Commercial Registry of Cadiz, Volume 2465, Page 113, Section 8, Sheet CA-61491',
      'Métodos de pago aceptados': 'Accepted payment methods',
      'Todos los derechos reservados.': 'All rights reserved.'
    },
    fr: {
      'Quiénes Somos': 'Qui sommes-nous',
      'Conozca nuestro equipo': 'Découvrez notre équipe',
      'Nuestro Blog': 'Notre blog',
      'Noticias y Novedades': 'Actualités et nouveautés',
      'Contact Rápido': 'Contact rapide',
      'Contacto Rápido': 'Contact rapide',
      'Línea Directa de Support': 'Ligne directe du support',
      'Línea Directa de Soporte': 'Ligne directe du support',
      'Servicio Técnico': 'Service technique',
      'Ofertas Activas': 'Offres actives',
      'Contactar Support': 'Contacter le support',
      'Contactar Soporte': 'Contacter le support',
      'Ayuda': 'Aide',
      'Preguntas Frecuentes': 'Questions fréquentes',
      'Política de Devoluciones': 'Politique de retour',
      'Envíos y Entregas': 'Livraison et expédition',
      'Reembolsos': 'Remboursements',
      'Buscar referencias': 'Rechercher des références',
      'Guías y enlaces clave': 'Guides et liens clés',
      'Cartuchos HP 364, 903 y 953XL': 'Cartouches HP 364, 903 et 953XL',
      'Cartucho o tóner: cuál elegir': 'Cartouche ou toner : que choisir',
      'Privacidad': 'Confidentialité',
      'Términos': 'Conditions',
      'Aviso legal': 'Mentions légales',
      'Información legal': 'Informations légales',
      'Resolución de litigios UE': 'Règlement des litiges UE',
      'Métodos de pago aceptados': 'Moyens de paiement acceptés',
      'Todos los derechos reservados.': 'Tous droits réservés.'
    },
    de: {
      'Quiénes Somos': 'Über uns',
      'Conozca nuestro equipo': 'Lerne unser Team kennen',
      'Nuestro Blog': 'Unser Blog',
      'Noticias y Novedades': 'News und Updates',
      'Contact Rápido': 'Schneller Kontakt',
      'Contacto Rápido': 'Schneller Kontakt',
      'Línea Directa de Support': 'Direkte Support-Hotline',
      'Línea Directa de Soporte': 'Direkte Support-Hotline',
      'Servicio Técnico': 'Technischer Service',
      'Ofertas Activas': 'Aktive Angebote',
      'Contactar Support': 'Support kontaktieren',
      'Contactar Soporte': 'Support kontaktieren',
      'Ayuda': 'Hilfe',
      'Preguntas Frecuentes': 'Häufige Fragen',
      'Política de Devoluciones': 'Rückgaberichtlinie',
      'Envíos y Entregas': 'Versand und Lieferung',
      'Reembolsos': 'Rückerstattungen',
      'Buscar referencias': 'Referenzen suchen',
      'Guías y enlaces clave': 'Leitfäden und wichtige Links',
      'Cartuchos HP 364, 903 y 953XL': 'HP-Patronen 364, 903 und 953XL',
      'Cartucho o tóner: cuál elegir': 'Patrone oder Toner: was wählen',
      'Privacidad': 'Datenschutz',
      'Términos': 'AGB',
      'Aviso legal': 'Impressum',
      'Información legal': 'Rechtliche Informationen',
      'Resolución de litigios UE': 'EU-Streitbeilegung',
      'Métodos de pago aceptados': 'Akzeptierte Zahlungsmethoden',
      'Todos los derechos reservados.': 'Alle Rechte vorbehalten.'
    },
    it: {
      'Quiénes Somos': 'Chi siamo',
      'Conozca nuestro equipo': 'Conosci il nostro team',
      'Nuestro Blog': 'Il nostro blog',
      'Noticias y Novedades': 'Notizie e novità',
      'Contact Rápido': 'Contatto rapido',
      'Contacto Rápido': 'Contatto rapido',
      'Línea Directa de Support': 'Linea diretta supporto',
      'Línea Directa de Soporte': 'Linea diretta supporto',
      'Servicio Técnico': 'Servizio tecnico',
      'Ofertas Activas': 'Offerte attive',
      'Contactar Support': 'Contatta il supporto',
      'Contactar Soporte': 'Contatta il supporto',
      'Ayuda': 'Aiuto',
      'Preguntas Frecuentes': 'Domande frequenti',
      'Política de Devoluciones': 'Politica di reso',
      'Envíos y Entregas': 'Spedizioni e consegne',
      'Reembolsos': 'Rimborsi',
      'Buscar referencias': 'Cerca riferimenti',
      'Guías y enlaces clave': 'Guide e link chiave',
      'Cartuchos HP 364, 903 y 953XL': 'Cartucce HP 364, 903 e 953XL',
      'Cartucho o tóner: cuál elegir': 'Cartuccia o toner: cosa scegliere',
      'Privacidad': 'Privacy',
      'Términos': 'Termini',
      'Aviso legal': 'Note legali',
      'Información legal': 'Informazioni legali',
      'Resolución de litigios UE': 'Risoluzione controversie UE',
      'Métodos de pago aceptados': 'Metodi di pagamento accettati',
      'Todos los derechos reservados.': 'Tutti i diritti riservati.'
    },
    nl: {
      'Quiénes Somos': 'Over ons',
      'Conozca nuestro equipo': 'Maak kennis met ons team',
      'Nuestro Blog': 'Onze blog',
      'Noticias y Novedades': 'Nieuws en updates',
      'Contact Rápido': 'Snel contact',
      'Contacto Rápido': 'Snel contact',
      'Línea Directa de Support': 'Directe supportlijn',
      'Línea Directa de Soporte': 'Directe supportlijn',
      'Servicio Técnico': 'Technische service',
      'Ofertas Activas': 'Actieve aanbiedingen',
      'Contactar Support': 'Support contacteren',
      'Contactar Soporte': 'Support contacteren',
      'Ayuda': 'Help',
      'Preguntas Frecuentes': 'Veelgestelde vragen',
      'Política de Devoluciones': 'Retourbeleid',
      'Envíos y Entregas': 'Verzending en levering',
      'Reembolsos': 'Terugbetalingen',
      'Buscar referencias': 'Referenties zoeken',
      'Guías y enlaces clave': 'Gidsen en belangrijke links',
      'Cartuchos HP 364, 903 y 953XL': 'HP-cartridges 364, 903 en 953XL',
      'Cartucho o tóner: cuál elegir': 'Cartridge of toner: wat kiezen',
      'Privacidad': 'Privacy',
      'Términos': 'Voorwaarden',
      'Aviso legal': 'Juridische kennisgeving',
      'Información legal': 'Juridische informatie',
      'Resolución de litigios UE': 'EU-geschillenbeslechting',
      'Métodos de pago aceptados': 'Geaccepteerde betaalmethoden',
      'Todos los derechos reservados.': 'Alle rechten voorbehouden.'
    },
    pt: {
      'Quiénes Somos': 'Sobre nós',
      'Conozca nuestro equipo': 'Conhece a nossa equipa',
      'Nuestro Blog': 'O nosso blog',
      'Noticias y Novedades': 'Notícias e novidades',
      'Contact Rápido': 'Contacto rápido',
      'Contacto Rápido': 'Contacto rápido',
      'Línea Directa de Support': 'Linha directa de apoio',
      'Línea Directa de Soporte': 'Linha directa de apoio',
      'Servicio Técnico': 'Serviço técnico',
      'Ofertas Activas': 'Ofertas activas',
      'Contactar Support': 'Contactar apoio',
      'Contactar Soporte': 'Contactar apoio',
      'Ayuda': 'Ajuda',
      'Preguntas Frecuentes': 'Perguntas frequentes',
      'Política de Devoluciones': 'Política de devoluções',
      'Envíos y Entregas': 'Envios e entregas',
      'Reembolsos': 'Reembolsos',
      'Buscar referencias': 'Procurar referências',
      'Guías y enlaces clave': 'Guias e links principais',
      'Cartuchos HP 364, 903 y 953XL': 'Cartuchos HP 364, 903 e 953XL',
      'Cartucho o tóner: cuál elegir': 'Cartucho ou toner: qual escolher',
      'Privacidad': 'Privacidade',
      'Términos': 'Termos',
      'Aviso legal': 'Aviso legal',
      'Información legal': 'Informação legal',
      'Resolución de litigios UE': 'Resolução de litígios UE',
      'Métodos de pago aceptados': 'Métodos de pagamento aceites',
      'Todos los derechos reservados.': 'Todos os direitos reservados.'
    }
  };

  function locale() {
    var seg = (location.pathname.split('/')[1] || '').toLowerCase();
    if (byLocale[seg]) return seg;
    var lang = (document.documentElement.getAttribute('lang') || '').slice(0, 2).toLowerCase();
    return byLocale[lang] ? lang : 'es';
  }

  function replaceInTextNodes(root, dict) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        var parent = node.parentElement;
        if (!parent || /^(script|style|noscript)$/i.test(parent.tagName)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (node) {
      var value = node.nodeValue;
      Object.keys(dict).forEach(function (from) {
        if (value.indexOf(from) !== -1) value = value.split(from).join(dict[from]);
      });
      node.nodeValue = value;
    });
  }

  function replaceAttributes(root, dict) {
    Array.prototype.forEach.call(root.querySelectorAll('[aria-label]'), function (el) {
      var value = el.getAttribute('aria-label') || '';
      Object.keys(dict).forEach(function (from) {
        if (value.indexOf(from) !== -1) value = value.split(from).join(dict[from]);
      });
      el.setAttribute('aria-label', value);
    });
  }

  function run() {
    var lang = locale();
    var dict = byLocale[lang];
    if (!dict) return;
    Array.prototype.forEach.call(document.querySelectorAll('.mdh-mega-wrapper.sm, footer.ft'), function (root) {
      replaceInTextNodes(root, dict);
      replaceAttributes(root, dict);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
